import { useEffect, useRef, useState } from "react";
import type { LayoutComponent } from "@scenix/core";

import { componentEngine, componentPicture, redrawEverySec } from "../lib/componentEngine";

/**
 * A box holding a component, drawn by the engine the worker uses (ADR 000069).
 *
 * Deliberately not a React tree. A component is a pure function of its props
 * and the time within the segment, and what this shows is the picture that
 * function makes, rasterised by the same WebAssembly that will rasterise it for
 * the video. Drawing it as JSX here would be a second implementation, and two
 * implementations of one description is where every divergence in this
 * codebase has come from.
 */
export function ComponentBox({
  component,
  intoSegmentSec,
  style,
  label,
}: {
  component: LayoutComponent;
  /** Where the segment is, in its own time. Null when nothing is playing. */
  intoSegmentSec: number | null;
  style: React.CSSProperties;
  label: string;
}) {
  const [picture, setPicture] = useState<string | null>(null);
  const [refused, setRefused] = useState<string | null>(null);
  const drawn = useRef<string | null>(null);

  // Quantised, so a playhead moving continuously does not ask for a new picture
  // sixty times a second. The moment asked for is still an exact multiple of
  // the step, so it is a frame the worker would draw rather than one only the
  // browser has.
  const at = intoSegmentSec === null ? 0 : Math.max(0, intoSegmentSec);
  const moment = Math.round(at / redrawEverySec) * redrawEverySec;

  // The identity of the picture wanted. Everything the engine reads is in here,
  // so a changed prop redraws for the same reason a changed moment does.
  const wanted = JSON.stringify([component.name, component.props ?? {}, moment]);

  useEffect(() => {
    if (drawn.current === wanted) return;
    let live = true;

    void (async () => {
      try {
        const engine = await componentEngine();
        if (!live) return;
        const png = await engine.draw(component.name, component.props ?? {}, moment, {
          // Drawn at a fixed size rather than the box's pixel size, so the
          // picture does not change every time somebody resizes the window,
          // and so the preview asks for the frame the render would make.
          widthPx: componentPicture.widthPx,
          heightPx: componentPicture.heightPx,
        });
        if (!live) return;
        drawn.current = wanted;
        setRefused(null);
        setPicture((old) => {
          if (old) URL.revokeObjectURL(old);
          return URL.createObjectURL(new Blob([png as BlobPart], { type: "image/png" }));
        });
      } catch (error) {
        if (!live) return;
        // Said out loud rather than left as an empty box. A component nothing
        // can draw is exactly the case ADR 000069 wants refused with a reason,
        // and the preview is where somebody is standing when they place one.
        setRefused(error instanceof Error ? error.message : String(error));
      }
    })();

    return () => {
      live = false;
    };
  }, [wanted, component.name, component.props, moment]);

  useEffect(
    () => () => {
      if (picture) URL.revokeObjectURL(picture);
      // Only on unmount: the effect above revokes the one it replaces.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [],
  );

  if (refused) {
    return (
      <span className="layout-box-empty muted" role="status">
        {refused}
      </span>
    );
  }
  if (!picture) {
    return <span className="layout-box-empty muted">{label || component.name}</span>;
  }
  return <img src={picture} alt={label || component.name} style={style} />;
}
